import { BrandLogo, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, ThemeScope } from '@bia-auction/ui';
import { AlertCircle } from 'lucide-react';
import { t } from '@bia-auction/shared-i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { BRAND } from '../lib/config';
import { PHOTOS } from '../lib/photos';

export function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A signed-in visitor has no business on the login form — send them to their own panel,
  // which is also where a fresh login lands (Bia feedback, 11.08.2026).
  useEffect(() => {
    if (state.authenticated) void navigate({ to: '/me' });
  }, [state.authenticated, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      void navigate({ to: '/me' });
    } catch (err) {
      // The approval gate's 403 arrives already localized — `api.ts` maps its stable `error`
      // label to the right copy, so "still under review" never reads as a bad password here.
      setError(err instanceof Error ? err.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    // Edge-to-edge: the route is `staticData.fullBleed`, so the shell hands over an
    // unpadded, full-width <main> and this fills it (`flex-1`) between header and footer.
    <div
      className="flex flex-1 items-center justify-center bg-navy-900 bg-hero bg-cover bg-center px-4 py-16"
      // Brand photography under the gradient (M18) — the group site's own login and banners
      // are photographic. Layered, not swapped, so a missing file leaves the plain gradient —
      // `bg-navy-900` is what makes that true: the veil is translucent, so without an opaque
      // floor a 404 on the photo would drop the whole surface onto the page background.
      style={{ backgroundImage: `var(--grad-hero-veil), url(/img/${PHOTOS.brandPlate})` }}
    >
      {/* Login is a fixed-dark surface (docs/15 §5) over the hero gradient. */}
      <ThemeScope theme="dark" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <BrandLogo variant="white" alt={BRAND} className="mb-2" />
          <CardTitle className="font-head text-display font-semibold">
            {t('bidder.login.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p
                role="alert"
                className="flex items-center gap-2 rounded-md bg-danger-tint px-3 py-2 text-sm text-danger-tint-fg"
              >
                <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
            {/* The one gradient CTA of the view (docs/15 §2). */}
            <Button type="submit" variant="brand" className="w-full" disabled={busy}>
              {busy ? t('login.submitting') : t('login.submit')}
            </Button>
            {/* M19: there was no recovery path at all before this. */}
            <Link
              to="/forgot-password"
              className="block rounded-sm text-center text-sm text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('reset.forgotLink')}
            </Link>
            <p className="text-center text-sm text-muted-foreground">
              {t('bidder.login.noAccount')}{' '}
              <Link to="/register" className="text-tx-brand hover:underline">
                {t('bidder.login.registerLink')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
